import {
  MagnifyingGlassIcon,
  PlusIcon,
  TrashIcon,
  TruckIcon,
  XMarkIcon,
} from '@heroicons/react/24/outline';
import { StarIcon } from '@heroicons/react/24/solid';
import React, { useEffect, useState } from 'react';
import { toast } from 'react-hot-toast';
import { apiService } from '../../services/api';
import { buildAvailableVehiclesEndpoint } from '../../services/collectionEndpoints';

interface Vehicle {
  id: string;
  vin: string;
  year: number;
  make: string;
  model: string;
  trim?: string;
  asking_price?: number;
  status: string;
  mileage: number;
}

interface InterestedVehicle {
  id: string;
  year: number;
  make: string;
  model: string;
  trim?: string;
  asking_price?: number;
  priority_order: number;
  notes?: string;
}

interface InterestedVehiclesModalProps {
  isOpen: boolean;
  onClose: () => void;
  customerId: string;
  customerName: string;
  interestedVehicles: InterestedVehicle[];
  onUpdate: () => void;
}

const InterestedVehiclesModal: React.FC<InterestedVehiclesModalProps> = ({
  isOpen,
  onClose,
  customerId,
  customerName,
  interestedVehicles,
  onUpdate,
}) => {
  const [vehicles, setVehicles] = useState<Vehicle[]>([]);
  const [filteredVehicles, setFilteredVehicles] = useState<Vehicle[]>([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [loading, setLoading] = useState(false);
  const [selectedVehicles, setSelectedVehicles] = useState<string[]>([]);

  useEffect(() => {
    if (isOpen) {
      fetchAvailableVehicles();
    }
  }, [isOpen]);

  useEffect(() => {
    // Filter vehicles based on search term and exclude already interested vehicles
    let filtered = vehicles.filter(
      (vehicle) => !interestedVehicles.some((iv) => iv.id === vehicle.id)
    );

    if (searchTerm.trim()) {
      const term = searchTerm.toLowerCase();
      filtered = filtered.filter(
        (vehicle) =>
          vehicle.make.toLowerCase().includes(term) ||
          vehicle.model.toLowerCase().includes(term) ||
          vehicle.vin.toLowerCase().includes(term) ||
          `${vehicle.year}`.includes(term)
      );
    }

    setFilteredVehicles(filtered);
  }, [vehicles, searchTerm, interestedVehicles]);

  const fetchAvailableVehicles = async () => {
    try {
      setLoading(true);
      const response = await apiService.get<Vehicle[]>(buildAvailableVehiclesEndpoint());
      setVehicles(response || []);
    } catch (error) {
      console.error('Error fetching vehicles:', error);
      toast.error('Failed to load vehicles');
      setVehicles([]);
    } finally {
      setLoading(false);
    }
  };

  const handleVehicleToggle = (vehicleId: string) => {
    setSelectedVehicles((prev) =>
      prev.includes(vehicleId) ? prev.filter((id) => id !== vehicleId) : [...prev, vehicleId]
    );
  };

  const handleAddVehicles = async () => {
    if (selectedVehicles.length === 0) {
      toast.error('Please select at least one vehicle');
      return;
    }

    try {
      setLoading(true);

      // Add each selected vehicle
      const promises = selectedVehicles.map((vehicleId, index) =>
        apiService.post(`/customers/${customerId}/interested-vehicles`, {
          vehicle_id: vehicleId,
          priority_order: interestedVehicles.length + index + 1,
          notes: '',
        })
      );

      await Promise.all(promises);

      toast.success(`Added ${selectedVehicles.length} vehicle(s) to interested list`);
      setSelectedVehicles([]);
      onUpdate();
    } catch (error) {
      console.error('Error adding interested vehicles:', error);
      toast.error('Failed to add vehicles');
    } finally {
      setLoading(false);
    }
  };

  const handleRemoveVehicle = async (vehicleId: string) => {
    try {
      await apiService.delete(`/customers/${customerId}/interested-vehicles/${vehicleId}`);
      toast.success('Vehicle removed from interested list');
      onUpdate();
    } catch (error) {
      console.error('Error removing interested vehicle:', error);
      toast.error('Failed to remove vehicle');
    }
  };

  const formatPrice = (price?: number) => {
    if (!price) return 'Contact for Price';
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      maximumFractionDigits: 0,
    }).format(price);
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div className="flex items-center justify-center min-h-screen px-4 pt-4 pb-20 text-center sm:block sm:p-0">
        <div
          className="fixed inset-0 transition-opacity bg-gray-500 bg-opacity-75"
          onClick={onClose}
        />

        <div className="inline-block w-full max-w-4xl p-6 my-8 overflow-hidden text-left align-middle transition-all transform bg-white shadow-xl rounded-lg">
          <div className="flex items-center justify-between mb-6">
            <div>
              <h3 className="text-lg font-medium text-gray-900 flex items-center">
                <TruckIcon className="w-6 h-6 mr-2 text-blue-600" />
                Interested Vehicles - {customerName}
              </h3>
              <p className="text-sm text-gray-500 mt-1">
                Manage vehicles this customer is interested in
              </p>
            </div>
            <button onClick={onClose} className="text-gray-400 hover:text-gray-500">
              <XMarkIcon className="w-6 h-6" />
            </button>
          </div>

          {/* Current Interested Vehicles */}
          {interestedVehicles.length > 0 && (
            <div className="mb-6">
              <h4 className="text-md font-medium text-gray-900 mb-3 flex items-center">
                <StarIcon className="w-5 h-5 mr-2 text-yellow-400" />
                Current Interested Vehicles ({interestedVehicles.length})
              </h4>
              <div className="space-y-2">
                {interestedVehicles
                  .sort((a, b) => a.priority_order - b.priority_order)
                  .map((vehicle) => (
                    <div
                      key={vehicle.id}
                      className="flex items-center justify-between p-3 bg-yellow-50 border border-yellow-200 rounded-lg"
                    >
                      <div className="flex items-center space-x-4">
                        <div className="flex items-center justify-center w-8 h-8 bg-yellow-100 text-yellow-600 rounded-full text-sm font-medium">
                          {vehicle.priority_order}
                        </div>
                        <div>
                          <div className="font-medium text-gray-900">
                            {vehicle.year} {vehicle.make} {vehicle.model} {vehicle.trim}
                          </div>
                          <div className="text-sm text-gray-500">
                            {formatPrice(vehicle.asking_price)}
                          </div>
                        </div>
                      </div>
                      <button
                        onClick={() => handleRemoveVehicle(vehicle.id)}
                        className="text-red-600 hover:text-red-800"
                        title="Remove from interested list"
                      >
                        <TrashIcon className="w-4 h-4" />
                      </button>
                    </div>
                  ))}
              </div>
            </div>
          )}

          {/* Add New Vehicles */}
          <div>
            <h4 className="text-md font-medium text-gray-900 mb-3">
              Add Vehicles to Interested List
            </h4>

            {/* Search */}
            <div className="mb-4">
              <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <MagnifyingGlassIcon className="h-5 w-5 text-gray-400" />
                </div>
                <input
                  type="text"
                  className="form-input pl-10 w-full"
                  placeholder="Search by make, model, VIN, or year..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                />
              </div>
            </div>

            {/* Vehicle List */}
            <div className="max-h-96 overflow-y-auto border border-gray-200 rounded-lg">
              {loading ? (
                <div className="text-center py-8">
                  <div className="text-gray-500">Loading vehicles...</div>
                </div>
              ) : filteredVehicles.length === 0 ? (
                <div className="text-center py-8">
                  <div className="text-gray-500">
                    {searchTerm ? 'No vehicles match your search' : 'No available vehicles'}
                  </div>
                </div>
              ) : (
                <div className="divide-y divide-gray-200">
                  {filteredVehicles.map((vehicle) => (
                    <label
                      key={vehicle.id}
                      className="flex items-center p-4 hover:bg-gray-50 cursor-pointer"
                    >
                      <input
                        type="checkbox"
                        className="form-checkbox h-4 w-4 text-blue-600"
                        checked={selectedVehicles.includes(vehicle.id)}
                        onChange={() => handleVehicleToggle(vehicle.id)}
                      />
                      <div className="ml-3 flex-1">
                        <div className="flex items-center justify-between">
                          <div>
                            <div className="font-medium text-gray-900">
                              {vehicle.year} {vehicle.make} {vehicle.model} {vehicle.trim}
                            </div>
                            <div className="text-sm text-gray-500">
                              VIN: {vehicle.vin} • {vehicle.mileage.toLocaleString()} miles
                            </div>
                          </div>
                          <div className="text-right">
                            <div className="font-medium text-gray-900">
                              {formatPrice(vehicle.asking_price)}
                            </div>
                            <div className="text-sm text-green-600">{vehicle.status}</div>
                          </div>
                        </div>
                      </div>
                    </label>
                  ))}
                </div>
              )}
            </div>
          </div>

          {/* Action Buttons */}
          <div className="flex justify-end space-x-3 mt-6 pt-4 border-t">
            <button type="button" onClick={onClose} className="btn-secondary">
              Close
            </button>
            {selectedVehicles.length > 0 && (
              <button onClick={handleAddVehicles} disabled={loading} className="btn-primary">
                <PlusIcon className="w-4 h-4 mr-2" />
                {loading ? 'Adding...' : `Add ${selectedVehicles.length} Vehicle(s)`}
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default InterestedVehiclesModal;
